import React, { useEffect, useState, useRef } from 'react';
import './SupremeCourtCaseForm.scss';
import {
  AddSupremeCourtManualCases,
  getBothCourtCasesDetailsByCourtRef,
  UpdateSupremeCourtManualCase,
  getScDataByCnr,
} from '../../services/api/CasesApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoading } from '../../context/LoadingContext';
import { useToast } from '../../context/ToastContext';
import { uploadFileWithProgress } from '../../utils/ftpUploadUtils';
import { useAuth } from '../../context/AuthContext';
import { FetchingOverlay } from '../../components/FetchingOverlay/FetchingOverlay';

export const SupremeCourtCaseForm = ({ isEdit = false }) => {
  const { company } = useAuth();
  const formRef = useRef(null);
  const submitSectionRef = useRef(null);
  const caseHistoryRefs = useRef([]);
  const iaDetailsRefs = useRef([]);
  const orderInfoRefs = useRef([]);
  const transferDocsRefs = useRef([]);
  const objectionDetailsRefs = useRef([]);
  const documentsRefs = useRef([]);
  const historyCaseDataRefs = useRef([]);
  const actsDataRefs = useRef([]);
  const fetchInputRef = useRef(null);
  const [fetchCnr, setFetchCnr] = useState('');
  const [uploadingOrders, setUploadingOrders] = useState({});
  const [uploadProgressOrders, setUploadProgressOrders] = useState({});
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  const [activeActsDataIndex, setActiveActsDataIndex] = useState(0);
  const [activeCaseHistoryIndex, setActiveCaseHistoryIndex] = useState(0);
  const [activeIaDetailsIndex, setActiveIaDetailsIndex] = useState(0);
  const [activeTransferDocsIndex, setActiveTransferDocsIndex] = useState(0);
  const [activeObjectionDetailsIndex, setActiveObjectionDetailsIndex] = useState(0);
  const [activeDocumentsIndex, setActiveDocumentsIndex] = useState(0);
  const [activeHistoryCaseDataIndex, setActiveHistoryCaseDataIndex] = useState(0);

  const [formData, setFormData] = useState({
    bench: '',
    caseLink: '',
    courtName: '',
    serialNumber: '',
    caseTypeAndNumber: '',
    parties: '',
    advocates: '',
    court: '',
    highCourt: '',
    currentAdvocate: '',
    viewDetails: {
      type: '',
      caseId: '',
      courtRef: '',
      index: '',
    },
    caseDetails: {
      'Filing Number': '',
      'Filing Date': '',
      'Registration Number': '',
      'Registration Date': '',
      'CNR Number': '',
      stampNumber: '',
    },
    caseStatus: {
      Status: '',
      'First Hearing Date': '',
      'Nature of Disposal': '',
      Coram: '',
      'Bench Type': '',
      'Judicial Branch': '',
      State: '',
      District: '',
    },
    caseHistory: [{ causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' }],
    actsData: [{ underAct: '', underSection: '' }],
    subordinateCourtInfo: {
      'Court Number and Name': '',
      'Case Number and Year': '',
      'Case Decision Date': '',
      State: '',
      District: '',
      judgeDesignation: '',
      judgementLanguage: '',
    },
    petitionerAndAdvocate: {
      petitioner: '',
      advocate: '',
      respondent: '',
      respondentAdvocate: '',
    },
    iaDetails: [{ iaNumber: '', party: '', filingDate: '', nextDate: '', iaStatus: '' }],
    orderInfo: [{ orderNumber: '', orderOn: '', judge: '', orderDate: '', orderPdfLink: '' }],
    transferDocs: [{ srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' }],
    objectionDetails: [{ srNo: '', scrutinyDate: '', objection: '', complianceDate: '', receiptDate: '' }],
    documents: [{ srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' }],
    historyCaseData: [{ causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' }],
  });

  const { showToast } = useToast();
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const { courtRef } = useParams();

  useEffect(() => {
    if (fetchInputRef?.current) {
      setTimeout(() => {
        try {
          fetchInputRef.current.focus();
          const val = fetchInputRef.current.value || '';
          fetchInputRef.current.setSelectionRange(val.length, val.length);
        } catch (e) {}
      }, 0);
    }
  }, [isEdit, courtRef]);

  // ── File upload helpers ──────────────────────────────────────────────────────

  const handleOrderFileUpload = async (selectedFile, index) => {
    if (!selectedFile) return;
    const orderKey = `order_${index}`;
    setUploadingOrders(prev => ({ ...prev, [orderKey]: true }));
    setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
    try {
      const filePath = company?._id
        ? `/${company._id}/sc/added/${formData.viewDetails.courtRef}/orders`
        : '/unknown/orders';
      await uploadFileWithProgress(
        selectedFile,
        filePath,
        (progress) => setUploadProgressOrders(prev => ({ ...prev, [orderKey]: progress })),
        (result) => {
          const updatedOrders = [...formData.orderInfo];
          updatedOrders[index].orderPdfLink = result.imageUrl;
          setFormData(prev => ({ ...prev, orderInfo: updatedOrders }));
          setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
          showToast('Order file uploaded successfully!', 'success');
        },
        (error) => {
          setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
          setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
          showToast(error.message || 'Order file upload failed', 'error');
        }
      );
    } catch (error) {
      setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
      setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
      showToast(error.message || 'Order file upload failed', 'error');
    }
  };

  const handleOrderFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleOrderFileUpload(selectedFile, index);
  };

  const handleRemoveOrderFile = (index) => {
    const updatedOrders = [...formData.orderInfo];
    updatedOrders[index].orderPdfLink = '';
    setFormData(prev => ({ ...prev, orderInfo: updatedOrders }));
    const orderKey = `order_${index}`;
    setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
    setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
  };

  // ── CNR fetch ────────────────────────────────────────────────────────────────

  const handleFetchByCnr = async (cnrValue) => {
    if (!cnrValue.trim()) {
      showToast('Please enter a CNR Number first', 'error');
      return;
    }
    setIsFetching(true);
    try {
      const response = await getScDataByCnr(cnrValue.trim(), company?._id);
      const resData = response.data;
      // SC endpoint returns { casesCount, cases: [...], cnrNumber, message }
      if (resData && resData.casesCount > 0 && resData.cases?.length > 0) {
        const caseObj = resData.cases[0];
        populateFormWithAPIData(caseObj, resData.cnrNumber);
        const missingFields = [];
        if (!caseObj.courtName) missingFields.push('Court Name');
        if (!caseObj.caseStatus || Object.keys(caseObj.caseStatus).length === 0)
          missingFields.push('Case Status');
        if (missingFields.length > 0) {
          showToast(
            `Case details fetched, but ${missingFields.join(' and ')} could not be retrieved. Please fill manually.`,
            'warning'
          );
        } else {
          showToast('Case details fetched and populated successfully!', 'success');
        }
      } else {
        showToast(resData?.message || 'No case details found for the provided CNR Number', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch case details. Please try again.', 'error');
    } finally {
      setIsFetching(false);
    }
  };

  // ── Floating submit button visibility ────────────────────────────────────────

  useEffect(() => {
    const submitSection = submitSectionRef.current;
    const submitSectionTop = document.querySelector('.hc-submit-section-top');
    const submitBtn = document.querySelector('.hc-submit-section-top .submit-btn');
    if (!submitSection || !submitSectionTop || !submitBtn) return;
    const observer = new IntersectionObserver(
      ([entry]) => { submitBtn.style.display = entry.isIntersecting ? 'none' : 'block'; },
      { root: null, threshold: 0.1 }
    );
    observer.observe(submitSection);
    return () => observer.unobserve(submitSection);
  }, []);

  const handleTopSubmit = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  // ── Edit mode: load existing case ────────────────────────────────────────────

  useEffect(() => {
    if (isEdit && courtRef) {
      showLoading('Loading case details...');
      getBothCourtCasesDetailsByCourtRef(courtRef)
        .then((response) => {
          if (response.data && response.data.case) {
            setFormData(response.data.case);
            showToast('Case details loaded successfully!', 'success');
          }
        })
        .catch(() => showToast('Failed to load case details. Please try again.', 'error'))
        .finally(() => hideLoading());
    }
  }, [isEdit, courtRef]);

  // ── Date helpers ─────────────────────────────────────────────────────────────

  const convertReadableDateToDDMMYYYY = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const monthMap = {
      january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
      july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
    };
    try {
      const cleaned = dateStr.toLowerCase().replace(/(\d+)(st|nd|rd|th)/g, '$1').trim();
      const parts = cleaned.split(/\s+/);
      if (parts.length !== 3) return dateStr;
      const [day, monthName, year] = parts;
      const monthNum = monthMap[monthName];
      if (!monthNum) return dateStr;
      return `${day.padStart(2, '0')}-${monthNum}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (/^\d{1,2}(st|nd|rd|th)?\s+\w+\s+\d{4}$/i.test(dateStr.trim())) {
      const ddmmyyyy = convertReadableDateToDDMMYYYY(dateStr);
      if (ddmmyyyy !== dateStr) {
        const parts = ddmmyyyy.split('-');
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateStr;
  };

  const formatToReadableDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    const dayNum = parseInt(day, 10);
    const monthIndex = parseInt(month, 10) - 1;
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return n + 'th';
      switch (n % 10) {
        case 1: return n + 'st';
        case 2: return n + 'nd';
        case 3: return n + 'rd';
        default: return n + 'th';
      }
    };
    return `${getOrdinal(dayNum)} ${monthNames[monthIndex]} ${year}`;
  };

  const formatToDDMMYYYY = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  };

  // ── Populate form from API response ──────────────────────────────────────────

  // caseObj = cases[0] from SC response, cnrNumber = top-level cnrNumber
  const populateFormWithAPIData = (caseObj, cnrNumber) => {
    if (!caseObj) return;
    const pa = caseObj.petitionerAndAdvocate || {};
    setFormData((prevData) => ({
      ...prevData,
      courtName: caseObj.courtName || '',
      bench: caseObj.bench || '',
      court: caseObj.firDetails?.court || caseObj.court || '',
      highCourt: caseObj.firDetails?.courtName || caseObj.highCourt || '',
      currentAdvocate: caseObj.firDetails?.currentAdvocate || caseObj.currentAdvocate || '',
      caseLink: caseObj.caseLink || '',
      parties: caseObj.parties || `${pa.petitioners?.[0] || ''} vs ${pa.respondents?.[0] || ''}`,
      advocates: Array.isArray(caseObj.advocates) ? caseObj.advocates.join(', ') : (caseObj.advocates || ''),
      serialNumber: caseObj.serialNumber ?? '',
      caseTypeAndNumber: caseObj.caseTypeAndNumber || '',
      viewDetails: {
        type: String(caseObj.viewDetails?.type ?? ''),
        caseId: String(caseObj.viewDetails?.caseId ?? ''),
        courtRef: String(caseObj.viewDetails?.courtRef ?? cnrNumber ?? ''),
        index: String(caseObj.viewDetails?.index ?? ''),
      },
      caseDetails: {
        'Filing Number': caseObj.caseDetails?.['Filing Number'] || '',
        // SC returns "17-10-2025 07:33 PM" — strip time, then convert DD-MM-YYYY → YYYY-MM-DD for date input
        'Filing Date': convertDateFormat((caseObj.caseDetails?.['Filing Date'] || '').split(' ')[0]) || '',
        'Registration Number': caseObj.caseDetails?.['Registration Number'] || '',
        // SC returns "22-01-2026" (DD-MM-YYYY) — convert to YYYY-MM-DD for date input
        'Registration Date': convertDateFormat(caseObj.caseDetails?.['Registration Date'] || '') || '',
        'CNR Number': caseObj.caseDetails?.['CNR Number'] || cnrNumber || '',
        stampNumber: caseObj.caseDetails?.stampNumber || '',
      },
      caseStatus: caseObj.caseStatus
        ? {
            Status: caseObj.caseStatus['Status'] || caseObj.caseStatus['Stage'] || caseObj.caseStatus['Case Status'] || '',
            'First Hearing Date': convertDateFormat(caseObj.caseStatus['First Hearing Date'] || '') || '',
            'Nature of Disposal': caseObj.caseStatus['Nature of Disposal'] || '',
            Coram: caseObj.caseStatus['Coram'] || '',
            'Bench Type': caseObj.caseStatus['Bench Type'] || '',
            'Judicial Branch': caseObj.caseStatus['Judicial Branch'] || '',
            State: caseObj.caseStatus['State'] || '',
            District: caseObj.caseStatus['District'] || '',
          }
        : prevData.caseStatus,
      caseHistory:
        caseObj.caseHistory?.length > 0
          ? caseObj.caseHistory.map((item) => ({
              causeListType: item.causeListType || '',
              judge: item.judge || '',
              businessOnDate: convertDateFormat(item.businessOnDate) || '',
              hearingDate: convertDateFormat(item.hearingDate) || '',
              purposeOfHearing: item.purposeOfHearing || '',
            }))
          : [{ causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' }],
      actsData:
        caseObj.actsData?.length > 0
          ? caseObj.actsData.map((item) => ({ underAct: item.underAct || '', underSection: item.underSection || '' }))
          : [{ underAct: '', underSection: '' }],
      subordinateCourtInfo: {
        'Court Number and Name': caseObj.subordinateCourtInfo?.['Court Number and Name'] || '',
        'Case Number and Year': caseObj.subordinateCourtInfo?.['Case Number and Year'] || '',
        'Case Decision Date': caseObj.subordinateCourtInfo?.['Case Decision Date'] || '',
        State: caseObj.subordinateCourtInfo?.State || caseObj.caseStatus?.State || '',
        District: caseObj.subordinateCourtInfo?.District || caseObj.caseStatus?.District || '',
        judgeDesignation: caseObj.subordinateCourtInfo?.judgeDesignation || '',
        judgementLanguage: caseObj.subordinateCourtInfo?.judgementLanguage || '',
      },
      petitionerAndAdvocate: {
        petitioner: pa.petitioner || pa.petitioners?.[0] || '',
        advocate: pa.advocate || pa.advocates?.[0] || '',
        respondent: pa.respondent || pa.respondents?.[0] || '',
        respondentAdvocate: pa.respondentAdvocate || pa.respondentAdvocates?.[0] || '',
      },
      iaDetails:
        caseObj.iaDetails?.length > 0
          ? caseObj.iaDetails
          : [{ iaNumber: '', party: '', filingDate: '', nextDate: '', iaStatus: '' }],
      orderInfo:
        caseObj.orderInfo?.length > 0
          ? caseObj.orderInfo.map((item) => ({
              orderNumber: item.orderNumber || '',
              orderOn: item.orderOn || item.orderDetails || '',
              judge: item.judge || '',
              orderDate: convertDateFormat(item.orderDate) || '',
              orderPdfLink: item.orderPdfLink || '',
            }))
          : [{ orderNumber: '', orderOn: '', judge: '', orderDate: '', orderPdfLink: '' }],
      transferDocs:
        caseObj.transferDocs?.length > 0
          ? caseObj.transferDocs.map((item) => ({
              srNo: item.srNo || '',
              documentNo: item.documentNo || '',
              dateOfReceiving: convertDateFormat(item.dateOfReceiving) || '',
              filedBy: item.filedBy || '',
              advocateName: item.advocateName || '',
              documentFiled: item.documentFiled || '',
            }))
          : [{ srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' }],
      objectionDetails:
        caseObj.objectionDetails?.length > 0
          ? caseObj.objectionDetails.map((item) => ({
              srNo: item.srNo || '',
              scrutinyDate: convertDateFormat(item.scrutinyDate) || '',
              objection: item.objection || '',
              complianceDate: convertDateFormat(item.complianceDate) || '',
              receiptDate: convertDateFormat(item.receiptDate) || '',
            }))
          : [{ srNo: '', scrutinyDate: '', objection: '', complianceDate: '', receiptDate: '' }],
      documents:
        caseObj.documents?.length > 0
          ? caseObj.documents
          : [{ srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' }],
      historyCaseData:
        caseObj.historyCaseData?.length > 0
          ? caseObj.historyCaseData.map((item) => ({
              causeListType: item.causeListType || '',
              judge: item.judge || '',
              businessOnDate: convertDateFormat(item.businessOnDate) || '',
              hearingDate: convertDateFormat(item.hearingDate) || '',
              purposeOfHearing: item.purposeOfHearing || '',
            }))
          : [{ causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' }],
    }));
  };

  // ── Form change handlers ─────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNestedChange = (e, section) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [section]: { ...formData[section], [name]: value } });
  };

  const handleArrayChange = (index, section, field, value) => {
    const updatedArray = [...formData[section]];
    updatedArray[index][field] = value;
    setFormData({ ...formData, [section]: updatedArray });
  };

  const addArrayItem = (section) => {
    const templates = {
      orderInfo: { orderNumber: '', orderOn: '', judge: '', orderDate: '', orderPdfLink: '' },
      caseHistory: { causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' },
      iaDetails: { iaNumber: '', party: '', filingDate: '', nextDate: '', iaStatus: '' },
      transferDocs: { srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' },
      objectionDetails: { srNo: '', scrutinyDate: '', objection: '', complianceDate: '', receiptDate: '' },
      documents: { srNo: '', documentNo: '', dateOfReceiving: '', filedBy: '', advocateName: '', documentFiled: '' },
      historyCaseData: { causeListType: '', judge: '', businessOnDate: '', hearingDate: '', purposeOfHearing: '' },
      actsData: { underAct: '', underSection: '' },
    };
    setFormData((prev) => {
      const newArray = [...prev[section], templates[section] || {}];
      if (section === 'orderInfo') setActiveOrderIndex(newArray.length - 1);
      else if (section === 'actsData') setActiveActsDataIndex(newArray.length - 1);
      else if (section === 'caseHistory') setActiveCaseHistoryIndex(newArray.length - 1);
      else if (section === 'iaDetails') setActiveIaDetailsIndex(newArray.length - 1);
      else if (section === 'transferDocs') setActiveTransferDocsIndex(newArray.length - 1);
      else if (section === 'objectionDetails') setActiveObjectionDetailsIndex(newArray.length - 1);
      else if (section === 'documents') setActiveDocumentsIndex(newArray.length - 1);
      else if (section === 'historyCaseData') setActiveHistoryCaseDataIndex(newArray.length - 1);
      const refMap = {
        caseHistory: caseHistoryRefs, iaDetails: iaDetailsRefs, orderInfo: orderInfoRefs,
        transferDocs: transferDocsRefs, objectionDetails: objectionDetailsRefs,
        documents: documentsRefs, historyCaseData: historyCaseDataRefs, actsData: actsDataRefs,
      };
      const refArray = refMap[section];
      if (refArray) setTimeout(() => { if (refArray.current[newArray.length - 1]) refArray.current[newArray.length - 1].focus(); }, 0);
      return { ...prev, [section]: newArray };
    });
  };

  const removeArrayItem = (index, section) => {
    const updatedArray = formData[section].filter((_, i) => i !== index);
    setFormData({ ...formData, [section]: updatedArray });
    const adjust = (current, setter) => {
      if (index === current) setter(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
      else if (index < current) setter(prev => prev - 1);
    };
    if (section === 'orderInfo') adjust(activeOrderIndex, setActiveOrderIndex);
    else if (section === 'actsData') adjust(activeActsDataIndex, setActiveActsDataIndex);
    else if (section === 'caseHistory') adjust(activeCaseHistoryIndex, setActiveCaseHistoryIndex);
    else if (section === 'iaDetails') adjust(activeIaDetailsIndex, setActiveIaDetailsIndex);
    else if (section === 'transferDocs') adjust(activeTransferDocsIndex, setActiveTransferDocsIndex);
    else if (section === 'objectionDetails') adjust(activeObjectionDetailsIndex, setActiveObjectionDetailsIndex);
    else if (section === 'documents') adjust(activeDocumentsIndex, setActiveDocumentsIndex);
    else if (section === 'historyCaseData') adjust(activeHistoryCaseDataIndex, setActiveHistoryCaseDataIndex);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    const partiesEmpty = !formData.parties?.trim();
    const courtRefEmpty = !formData.viewDetails.courtRef?.trim();
    const courtNameEmpty = !formData.courtName?.trim();

    if (courtNameEmpty && partiesEmpty && courtRefEmpty) {
      showToast('Court Name, Parties and Court Reference are required.', 'error');
      return;
    }
    if ((partiesEmpty && courtRefEmpty) || (partiesEmpty && courtNameEmpty) || (courtNameEmpty && courtRefEmpty)) {
      const missing = [];
      if (courtNameEmpty) missing.push('Court Name');
      if (partiesEmpty) missing.push('Parties');
      if (courtRefEmpty) missing.push('Court Reference');
      showToast(`${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required.`, 'error');
      return;
    }
    if (courtNameEmpty) { showToast('Court Name field is required.', 'error'); return; }
    if (partiesEmpty) { showToast('Parties field is required.', 'error'); return; }
    if (courtRefEmpty) { showToast('Court Reference field is required.', 'error'); return; }

    showLoading(isEdit ? 'Updating case...' : 'Submitting case...');
    try {
      const processedCaseStatus = { ...formData.caseStatus };
      if (processedCaseStatus['First Hearing Date']) {
        processedCaseStatus['First Hearing Date'] = formatToReadableDate(processedCaseStatus['First Hearing Date']);
      }
      const filteredCaseStatus = {};
      Object.keys(processedCaseStatus).forEach(key => {
        if (processedCaseStatus[key]?.trim()) filteredCaseStatus[key] = processedCaseStatus[key];
      });

      const processedCaseDetails = { ...formData.caseDetails };
      ['Filing Date', 'Registration Date'].forEach(field => {
        if (processedCaseDetails[field]) processedCaseDetails[field] = formatToDDMMYYYY(processedCaseDetails[field]);
      });

      const finalCaseObject = {
        ...formData,
        caseStatus: filteredCaseStatus,
        caseDetails: processedCaseDetails,
        serialNumber: Number(formData.serialNumber),
        viewDetails: {
          type: String(formData.viewDetails.type ?? ''),
          caseId: String(formData.viewDetails.caseId ?? ''),
          courtRef: String(formData.viewDetails.courtRef ?? ''),
          index: String(formData.viewDetails.index ?? ''),
        },
      };

      if (isEdit && courtRef) {
        await UpdateSupremeCourtManualCase(courtRef, finalCaseObject);
        showToast('Case updated successfully', 'success');
      } else {
        await AddSupremeCourtManualCases({ cases: [finalCaseObject] });
        showToast('Case submitted successfully', 'success');
      }
      setTimeout(() => navigate('/home/cases'), 1500);
    } catch (error) {
      console.error('Submission error:', error?.response?.data || error.message);
      showToast('Failed to submit. Please check all fields and try again.', 'error');
    } finally {
      hideLoading();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="high-court-case-form-container">
        <div className="hc-form-header">
          <h1 className="hc-form-title">{isEdit ? 'Edit Supreme Court Case' : 'Supreme Court Case Form'}</h1>
          <p className="hc-form-subtitle">Complete case information management system</p>
        </div>

        {/* ── CNR Fetch ── */}
        <div className="case-type-fetch-section">
          <h3 className="fetch-section-title">Fetch Case Details by CNR</h3>
          <div className="fetch-input-group">
            <div className="fetch-input-wrapper">
              <input
                ref={fetchInputRef}
                className="fetch-input"
                type="text"
                value={fetchCnr}
                onChange={(e) => setFetchCnr(e.target.value.replace(/[-\s.\\/]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isFetching && fetchCnr.trim()) {
                    e.preventDefault();
                    handleFetchByCnr(fetchCnr);
                  }
                }}
                placeholder="Enter CNR Number to fetch details.   E.g. (SCIN010115512019)"
              />
            </div>
            <button
              type="button"
              className="fetch-details-btn"
              onClick={() => handleFetchByCnr(fetchCnr)}
              disabled={isFetching || !fetchCnr.trim()}
            >
              {isFetching ? 'Fetching...' : 'Fetch Details'}
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          ref={formRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit' && e.target.type !== 'button') {
              e.preventDefault();
              formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }
          }}
        >
          {/* ── Basic Details ── */}
          <h2 className="hc-title">Basic Details</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Court Name <span className="user-form-required">*</span></label>
                <input name="courtName" value={formData.courtName} onChange={handleChange} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Case Type &amp; Number</label>
                <input name="caseTypeAndNumber" value={formData.caseTypeAndNumber} onChange={handleChange} />
              </div>
              <div className="form-det">
                <label>Serial Number</label>
                <input name="serialNumber" value={formData.serialNumber} onChange={handleChange} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Parties <span className="user-form-required">*</span></label>
                <input name="parties" value={formData.parties} onChange={handleChange} />
              </div>
              <div className="form-det">
                <label>Advocates</label>
                <input name="advocates" value={formData.advocates} onChange={handleChange} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Bench</label>
                <input name="bench" value={formData.bench} onChange={handleChange} />
              </div>
              <div className="form-det">
                <label>Court</label>
                <input name="court" value={formData.court} onChange={handleChange} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>High Court</label>
                <input name="highCourt" value={formData.highCourt} onChange={handleChange} />
              </div>
              <div className="form-det">
                <label>Current Advocate</label>
                <input name="currentAdvocate" value={formData.currentAdvocate} onChange={handleChange} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Case Link</label>
                <input name="caseLink" value={formData.caseLink} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* ── View Details ── */}
          <h2 className="hc-title">View Details</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Type</label>
                <input name="type" value={formData.viewDetails.type} onChange={(e) => handleNestedChange(e, 'viewDetails')} />
              </div>
              <div className="form-det">
                <label>Case ID</label>
                <input name="caseId" value={formData.viewDetails.caseId} onChange={(e) => handleNestedChange(e, 'viewDetails')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Court Reference <span className="user-form-required">*</span></label>
                <input name="courtRef" value={formData.viewDetails.courtRef} onChange={(e) => handleNestedChange(e, 'viewDetails')} />
              </div>
              <div className="form-det">
                <label>Index</label>
                <input name="index" value={formData.viewDetails.index} onChange={(e) => handleNestedChange(e, 'viewDetails')} />
              </div>
            </div>
          </div>

          {/* ── Case Details ── */}
          <h2 className="hc-title">Case Details</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Filing Number</label>
                <input name="Filing Number" value={formData.caseDetails['Filing Number']} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
              <div className="form-det">
                <label>Filing Date</label>
                <input type="date" name="Filing Date" value={formData.caseDetails['Filing Date']} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Registration Number</label>
                <input name="Registration Number" value={formData.caseDetails['Registration Number']} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
              <div className="form-det">
                <label>Registration Date</label>
                <input type="date" name="Registration Date" value={formData.caseDetails['Registration Date']} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>CNR Number</label>
                <input name="CNR Number" value={formData.caseDetails['CNR Number']} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
              <div className="form-det">
                <label>Stamp Number</label>
                <input name="stampNumber" value={formData.caseDetails.stampNumber} onChange={(e) => handleNestedChange(e, 'caseDetails')} />
              </div>
            </div>
          </div>

          {/* ── Case Status ── */}
          <h2 className="hc-title">Case Status</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Status</label>
                <select name="Status" value={formData.caseStatus.Status} onChange={(e) => handleNestedChange(e, 'caseStatus')}>
                  <option value="">Select Status</option>
                  <option value="PENDING">PENDING</option>
                  <option value="DISPOSED">DISPOSED</option>
                </select>
              </div>
              <div className="form-det">
                <label>First Hearing Date</label>
                <input type="date" name="First Hearing Date" value={formData.caseStatus['First Hearing Date']} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Nature of Disposal</label>
                <input name="Nature of Disposal" value={formData.caseStatus['Nature of Disposal']} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
              <div className="form-det">
                <label>Coram</label>
                <input name="Coram" value={formData.caseStatus.Coram} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Bench Type</label>
                <input name="Bench Type" value={formData.caseStatus['Bench Type']} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
              <div className="form-det">
                <label>Judicial Branch</label>
                <input name="Judicial Branch" value={formData.caseStatus['Judicial Branch']} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>State</label>
                <input name="State" value={formData.caseStatus.State} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
              <div className="form-det">
                <label>District</label>
                <input name="District" value={formData.caseStatus.District} onChange={(e) => handleNestedChange(e, 'caseStatus')} />
              </div>
            </div>
          </div>

          {/* ── Orders ── */}
          <h2 className="hc-title">Orders</h2>
          {formData.orderInfo.map((item, index) => {
            const orderKey = `order_${index}`;
            const isUploading = uploadingOrders[orderKey];
            const uploadProgress = uploadProgressOrders[orderKey] || 0;
            const isExpanded = index === activeOrderIndex;
            const hasData = item.orderNumber || item.orderOn || item.judge || item.orderDate || item.orderPdfLink;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Order No:</span><span className="summary-value">{item.orderNumber || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Date:</span><span className="summary-value">{item.orderDate || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveOrderIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'orderInfo')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Order Number</label>
                          <input ref={(el) => (orderInfoRefs.current[index] = el)} type="text" value={item.orderNumber} onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderNumber', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Order On</label>
                          <input type="text" value={item.orderOn} onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderOn', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Judge</label>
                          <input type="text" value={item.judge} onChange={(e) => handleArrayChange(index, 'orderInfo', 'judge', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Order Date</label>
                          <input type="date" value={item.orderDate} onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderDate', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Order PDF</label>
                          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov" onChange={(e) => handleOrderFileChange(e, index)} style={{ display: 'none' }} id={`sc_orderFile_${index}`} disabled={isUploading} />
                          {!item.orderPdfLink && !isUploading && (
                            <div className="upload-placeholder" onClick={() => document.getElementById(`sc_orderFile_${index}`).click()} style={{ border: '2px dashed #e2e8f0', borderRadius: '0.375rem', padding: '1rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
                              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Click to upload order file</span>
                            </div>
                          )}
                          {isUploading && (
                            <div className="upload-progress">
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden', marginBottom: '0.5rem' }}>
                                <div style={{ width: `${uploadProgress}%`, height: '100%', backgroundColor: '#3b82f6', transition: 'width 0.3s ease' }} />
                              </div>
                              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>{uploadProgress}% uploaded</p>
                            </div>
                          )}
                          {item.orderPdfLink && !isUploading && (
                            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '0.375rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                              <a href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${item.orderPdfLink}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1', textDecoration: 'none', fontWeight: '500', fontSize: '0.875rem', flex: 1 }}>View Uploaded Order File</a>
                              <button type="button" onClick={() => handleRemoveOrderFile(index)} style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '0.375rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer' }}>Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'orderInfo')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('orderInfo')}>+ Order</button>
          </div>

          {/* ── Acts Data ── */}
          <h2 className="hc-title">Acts Data</h2>
          {formData.actsData.map((item, index) => {
            const isExpanded = index === activeActsDataIndex;
            const hasData = item.underAct || item.underSection;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Act:</span><span className="summary-value">{item.underAct || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Section:</span><span className="summary-value">{item.underSection || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveActsDataIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'actsData')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Act</label>
                          <input ref={(el) => (actsDataRefs.current[index] = el)} value={item.underAct} onChange={(e) => handleArrayChange(index, 'actsData', 'underAct', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Section</label>
                          <input value={item.underSection} onChange={(e) => handleArrayChange(index, 'actsData', 'underSection', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'actsData')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('actsData')}>+ Acts Data</button>
          </div>

          {/* ── Subordinate Court Info ── */}
          <h2 className="hc-title">Subordinate Court Info</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Court Number and Name</label>
                <input name="Court Number and Name" value={formData.subordinateCourtInfo['Court Number and Name']} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
              <div className="form-det">
                <label>Case Number and Year</label>
                <input name="Case Number and Year" value={formData.subordinateCourtInfo['Case Number and Year']} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Case Decision Date</label>
                <input type="date" name="Case Decision Date" value={formData.subordinateCourtInfo['Case Decision Date']} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
              <div className="form-det">
                <label>State</label>
                <input name="State" value={formData.subordinateCourtInfo.State} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>District</label>
                <input name="District" value={formData.subordinateCourtInfo.District} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
              <div className="form-det">
                <label>Judge Designation</label>
                <input name="judgeDesignation" value={formData.subordinateCourtInfo.judgeDesignation} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Judgement Language</label>
                <input name="judgementLanguage" value={formData.subordinateCourtInfo.judgementLanguage} onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')} />
              </div>
            </div>
          </div>

          {/* ── Petitioner And Advocate ── */}
          <h2 className="hc-title">Petitioner And Advocate</h2>
          <div className="div-rem">
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Petitioner</label>
                <input name="petitioner" value={formData.petitionerAndAdvocate.petitioner} onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')} />
              </div>
              <div className="form-det">
                <label>Advocate</label>
                <input name="advocate" value={formData.petitionerAndAdvocate.advocate} onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')} />
              </div>
            </div>
            <div className="high-court-form-group">
              <div className="form-det">
                <label>Respondent</label>
                <input name="respondent" value={formData.petitionerAndAdvocate.respondent} onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')} />
              </div>
              <div className="form-det">
                <label>Respondent Advocate</label>
                <input name="respondentAdvocate" value={formData.petitionerAndAdvocate.respondentAdvocate} onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')} />
              </div>
            </div>
          </div>

          {/* ── Case History ── */}
          <h2 className="hc-title">Case History</h2>
          {formData.caseHistory.map((item, index) => {
            const isExpanded = index === activeCaseHistoryIndex;
            const hasData = item.purposeOfHearing || item.hearingDate || item.judge;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Purpose:</span><span className="summary-value">{item.purposeOfHearing || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Hearing Date:</span><span className="summary-value">{item.hearingDate || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveCaseHistoryIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'caseHistory')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-row">
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Cause List Type</label>
                          <input ref={(el) => (caseHistoryRefs.current[index] = el)} value={item.causeListType} onChange={(e) => handleArrayChange(index, 'caseHistory', 'causeListType', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Judge</label>
                          <input value={item.judge} onChange={(e) => handleArrayChange(index, 'caseHistory', 'judge', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Business On Date</label>
                          <input type="date" value={item.businessOnDate} onChange={(e) => handleArrayChange(index, 'caseHistory', 'businessOnDate', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Hearing Date</label>
                          <input type="date" value={item.hearingDate} onChange={(e) => handleArrayChange(index, 'caseHistory', 'hearingDate', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Purpose of Hearing</label>
                          <input value={item.purposeOfHearing} onChange={(e) => handleArrayChange(index, 'caseHistory', 'purposeOfHearing', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'caseHistory')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('caseHistory')}>+ Case History</button>
          </div>

          {/* ── IA Details ── */}
          <h2 className="hc-title">IA Details</h2>
          {formData.iaDetails.map((item, index) => {
            const isExpanded = index === activeIaDetailsIndex;
            const hasData = item.iaNumber || item.party || item.iaStatus;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">IA No:</span><span className="summary-value">{item.iaNumber || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Status:</span><span className="summary-value">{item.iaStatus || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveIaDetailsIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'iaDetails')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>IA Number</label>
                          <input ref={(el) => (iaDetailsRefs.current[index] = el)} type="text" value={item.iaNumber} onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaNumber', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Party</label>
                          <input type="text" value={item.party} onChange={(e) => handleArrayChange(index, 'iaDetails', 'party', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Filing Date</label>
                          <input type="date" value={item.filingDate} onChange={(e) => handleArrayChange(index, 'iaDetails', 'filingDate', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Next Date</label>
                          <input type="date" value={item.nextDate} onChange={(e) => handleArrayChange(index, 'iaDetails', 'nextDate', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>IA Status</label>
                          <input type="text" value={item.iaStatus} onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaStatus', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'iaDetails')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('iaDetails')}>+ IA Detail</button>
          </div>

          {/* ── Other Documents (Interlocutory Application Documents - OTHER table) ── */}
          <h2 className="hc-title">Other Documents</h2>
          {formData.transferDocs.map((item, index) => {
            const isExpanded = index === activeTransferDocsIndex;
            const hasData = item.documentNo || item.documentFiled || item.filedBy;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Doc No:</span><span className="summary-value">{item.documentNo || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Document Type:</span><span className="summary-value">{item.documentFiled || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveTransferDocsIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'transferDocs')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Document Number</label>
                          <input ref={(el) => (transferDocsRefs.current[index] = el)} type="text" value={item.documentNo} onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentNo', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Filing Date</label>
                          <input type="date" value={item.dateOfReceiving} onChange={(e) => handleArrayChange(index, 'transferDocs', 'dateOfReceiving', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Filed By</label>
                          <input type="text" value={item.filedBy} onChange={(e) => handleArrayChange(index, 'transferDocs', 'filedBy', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Entered By</label>
                          <input type="text" value={item.advocateName} onChange={(e) => handleArrayChange(index, 'transferDocs', 'advocateName', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Document Type</label>
                          <input type="text" value={item.documentFiled} onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentFiled', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'transferDocs')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('transferDocs')}>+ Other Document</button>
          </div>

          {/* ── Objection Details ── */}
          <h2 className="hc-title">Objection Details</h2>
          {formData.objectionDetails.map((item, index) => {
            const isExpanded = index === activeObjectionDetailsIndex;
            const hasData = item.objection || item.scrutinyDate;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Objection:</span><span className="summary-value">{item.objection || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Scrutiny Date:</span><span className="summary-value">{item.scrutinyDate || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveObjectionDetailsIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'objectionDetails')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Scrutiny Date</label>
                          <input ref={(el) => (objectionDetailsRefs.current[index] = el)} type="date" value={item.scrutinyDate} onChange={(e) => handleArrayChange(index, 'objectionDetails', 'scrutinyDate', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Objection</label>
                          <input value={item.objection} onChange={(e) => handleArrayChange(index, 'objectionDetails', 'objection', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Compliance Date</label>
                          <input type="date" value={item.complianceDate} onChange={(e) => handleArrayChange(index, 'objectionDetails', 'complianceDate', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Receipt Date</label>
                          <input type="date" value={item.receiptDate} onChange={(e) => handleArrayChange(index, 'objectionDetails', 'receiptDate', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'objectionDetails')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('objectionDetails')}>+ Objection Details</button>
          </div>

          {/* ── Documents ── */}
          <h2 className="hc-title">Documents</h2>
          {formData.documents.map((item, index) => {
            const isExpanded = index === activeDocumentsIndex;
            const hasData = item.documentNo || item.documentFiled || item.filedBy;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Doc No:</span><span className="summary-value">{item.documentNo || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Document:</span><span className="summary-value">{item.documentFiled || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveDocumentsIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'documents')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Document Number</label>
                          <input ref={(el) => (documentsRefs.current[index] = el)} type="text" value={item.documentNo} onChange={(e) => handleArrayChange(index, 'documents', 'documentNo', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Date of Receiving</label>
                          <input type="date" value={item.dateOfReceiving} onChange={(e) => handleArrayChange(index, 'documents', 'dateOfReceiving', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Filed By</label>
                          <input type="text" value={item.filedBy} onChange={(e) => handleArrayChange(index, 'documents', 'filedBy', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Advocate Name</label>
                          <input type="text" value={item.advocateName} onChange={(e) => handleArrayChange(index, 'documents', 'advocateName', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Document Filed</label>
                          <input type="text" value={item.documentFiled} onChange={(e) => handleArrayChange(index, 'documents', 'documentFiled', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'documents')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('documents')}>+ Document</button>
          </div>

          {/* ── History Case Data ── */}
          <h2 className="hc-title">History Case Data</h2>
          {formData.historyCaseData.map((item, index) => {
            const isExpanded = index === activeHistoryCaseDataIndex;
            const hasData = item.purposeOfHearing || item.hearingDate || item.judge;
            return (
              <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                {!isExpanded && hasData ? (
                  <div className="order-summary-view">
                    <div className="summary-details">
                      <div className="summary-item"><span className="summary-label">Purpose:</span><span className="summary-value">{item.purposeOfHearing || 'N/A'}</span></div>
                      <div className="summary-item"><span className="summary-label">Hearing Date:</span><span className="summary-value">{item.hearingDate || 'N/A'}</span></div>
                    </div>
                    <div className="summary-actions">
                      <button type="button" className="edit-btn" onClick={() => setActiveHistoryCaseDataIndex(index)}>Edit</button>
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'historyCaseData')}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Cause List Type</label>
                          <input ref={(el) => (historyCaseDataRefs.current[index] = el)} type="text" value={item.causeListType} onChange={(e) => handleArrayChange(index, 'historyCaseData', 'causeListType', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Judge</label>
                          <input type="text" value={item.judge} onChange={(e) => handleArrayChange(index, 'historyCaseData', 'judge', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Business On Date</label>
                          <input type="date" value={item.businessOnDate} onChange={(e) => handleArrayChange(index, 'historyCaseData', 'businessOnDate', e.target.value)} />
                        </div>
                        <div className="form-det">
                          <label>Hearing Date</label>
                          <input type="date" value={item.hearingDate} onChange={(e) => handleArrayChange(index, 'historyCaseData', 'hearingDate', e.target.value)} />
                        </div>
                      </div>
                      <div className="high-court-form-group">
                        <div className="form-det">
                          <label>Purpose of Hearing</label>
                          <input type="text" value={item.purposeOfHearing} onChange={(e) => handleArrayChange(index, 'historyCaseData', 'purposeOfHearing', e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="rem-div-btn">
                      <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'historyCaseData')}>Remove</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="add-div-btn">
            <button type="button" className="court-add-case" onClick={() => addArrayItem('historyCaseData')}>+ History Case Data</button>
          </div>

          <div className="hc-submit-section" ref={submitSectionRef}>
            <button className="submit-btn" type="submit" disabled={isLoading}>
              <span>{isLoading ? 'Processing...' : isEdit ? 'Update Supreme Court Case' : 'Submit Supreme Court Case'}</span>
            </button>
          </div>
        </form>

        <div className="hc-submit-section-top">
          <button className="submit-btn" type="button" onClick={handleTopSubmit} disabled={isLoading}>
            <span>{isLoading ? 'Processing...' : isEdit ? 'Update Supreme Court Case' : 'Submit Supreme Court Case'}</span>
          </button>
        </div>
      </div>

      <FetchingOverlay isVisible={isFetching} message="Fetching Supreme Court Case Details" />
    </>
  );
};
